import React, { Suspense, Component } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { ThemeProvider } from './contexts/ThemeContext';
import { canAccessRoute, isPostventaHiddenForTenant } from './config/permissions';
import Layout from './components/Layout';
import TestingBadge from './components/TestingBadge';
import Dashboard from './pages/Dashboard';
// DashboardConfigurator import removed — /analytics uses static Analytics layout
import Agents from './pages/Agents';
import AgentEditor from './pages/AgentEditor';
import Calls from './pages/Calls';
import CallHistory from './pages/CallHistory';
import Analytics from './pages/Analytics';
import Team from './pages/Team';
import SettingsPage from './pages/settings/SettingsPage';
import PhoneNumbers from './pages/PhoneNumbers';
import Tools from './pages/Tools';
import Docs from './pages/Docs';
import Login from './pages/Login';
import Invite from './pages/Invite';
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword from './pages/ResetPassword';
// Campaign pages
import WhatsAppPage from './pages/whatsapp/WhatsAppPage';
import SupportPage from './pages/support/SupportPage';
import PostventaPage from './pages/postventa/PostventaPage';
import CalendarPage from './pages/calendar/CalendarPage';
import CampaignsList from './pages/campaigns/CampaignsList';
import CampaignWizard from './pages/campaigns/CampaignWizard';
import CampaignDetail from './pages/campaigns/CampaignDetail';

// A translator (Chrome/Edge auto-translate) or DOM-mutating extension re-parents
// React-owned text nodes, so React's reconciliation later calls
// parent.removeChild(child) on a node it no longer owns:
//   NotFoundError: Failed to execute 'removeChild' on 'Node':
//   The node to be removed is not a child of this node.
// index.html now sets translate="no" + <meta name="google" content="notranslate">
// and App() marks its root translate="no" to prevent this. This helper lets the
// boundary recover ONCE (single reload) if a browser extension still mutates the
// DOM despite the opt-out, instead of dead-ending on a whitescreen. It never
// swallows unrelated render errors or globally patches Node.
const REMOVECHILD_RECOVER_KEY = 'qomp-removechild-translate-reload';
// Only the specific DOM-reparenting signature that browser auto-translate / DOM-mutating
// extensions produce should trigger the one-shot reload recovery. A bare NotFoundError
// name is NOT enough (many unrelated failures use that name), so we require the
// removeChild/insertBefore/"not a child of this node" message signature. This keeps the
// recovery scoped to the translate-removeChild class and never reloads unrelated errors.
function isBrowserDomMutationError(error) {
  if (!error) return false;
  const name = error.name || '';
  const message = error.message || '';
  const hasReparentSignature = /removeChild|insertBefore|not a child of this node/i.test(
    message
  );
  return hasReparentSignature && (name === 'NotFoundError' || name === 'HierarchyRequestError' || name === '' || name === 'Error');
}

// Top-level error boundary — prevents render crashes from whitescreening the app
// Exported so the boundary's recovery lifecycle can be regression-tested directly.
export class ErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, info) {
    console.error('[ErrorBoundary] Caught render error:', error, info);
    // A boundary that caught an error must NOT clear the one-shot marker on its
    // settle timer (see componentDidMount) — the error persisted across the reload.
    this._caughtError = true;
    if (this._settleTimer && typeof window !== 'undefined') {
      window.clearTimeout(this._settleTimer);
      this._settleTimer = null;
    }
    // If a browser translation/extension corrupted the DOM under React, a single
    // fresh reload restores a clean tree. The sessionStorage marker is set BEFORE
    // the reload and is only cleared after a genuinely healthy settle (no error),
    // so a persistent error that re-throws right after the recovery reload finds the
    // marker already '1' and falls through to the manual "Refresh page" button
    // instead of reloading again. This bounds automatic recovery to exactly ONE reload.
    if (isBrowserDomMutationError(error) && typeof window !== 'undefined') {
      try {
        if (window.sessionStorage.getItem(REMOVECHILD_RECOVER_KEY) !== '1') {
          window.sessionStorage.setItem(REMOVECHILD_RECOVER_KEY, '1');
          window.setTimeout(() => window.location.reload(), 150);
        }
        // else: already recovered once for this error — do NOT reload again.
      } catch (_) {
        // sessionStorage unavailable (private mode) — fall through to manual button.
      }
    }
  }

  componentDidMount() {
    // Clear the one-shot recovery marker ONLY after the app has settled cleanly
    // (mounted AND no error caught within a short window). Clearing unconditionally
    // on every mount — including the mount right after a recovery reload — would let
    // a persistent error re-arm another reload every cycle → infinite reload loop.
    // Deferring the clear and cancelling it in componentDidCatch makes the marker
    // survive across the reload for a still-failing tree, keeping recovery one-shot.
    if (typeof window === 'undefined') return;
    this._settleTimer = window.setTimeout(() => {
      this._settleTimer = null;
      if (this._caughtError) return; // error persisted — keep marker to block re-reload
      try {
        window.sessionStorage.removeItem(REMOVECHILD_RECOVER_KEY);
      } catch (_) {
        /* ignore */
      }
    }, 1000);
  }

  componentWillUnmount() {
    if (this._settleTimer && typeof window !== 'undefined') {
      window.clearTimeout(this._settleTimer);
      this._settleTimer = null;
    }
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="min-h-screen flex flex-col items-center justify-center bg-background text-foreground gap-4 p-8">
          <h2 className="text-xl font-semibold">Something went wrong</h2>
          <p className="text-sm text-muted text-center max-w-md">
            An unexpected error occurred. Please refresh the page or contact support if the problem persists.
          </p>
          {this.state.error?.message && (
            <p className="text-xs text-muted text-center max-w-md font-mono opacity-70">
              {this.state.error.message}
            </p>
          )}
          <button
            onClick={() => window.location.reload()}
            className="px-4 py-2 bg-primary text-white rounded-full text-sm font-medium hover:opacity-90 transition-opacity"
          >
            Refresh page
          </button>
        </div>
      );
    }
    return this.props.children;
  }
}

/**
 * AppErrorBoundary — top-level safety net.
 * Catches any unhandled React render/lifecycle error and renders a minimal
 * fallback instead of wiping the entire app (blank screen).
 * Does NOT catch async errors (use try/catch in event handlers for those).
 */
class AppErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, errorMessage: '' };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, errorMessage: error?.message || 'Unknown error' };
  }

  componentDidCatch(error, info) {
    // Log to console so it shows in browser DevTools / ECS logs.
    console.error('[AppErrorBoundary] Caught unhandled error:', error, info?.componentStack);
  }

  handleReload = () => {
    this.setState({ hasError: false, errorMessage: '' });
    window.location.reload();
  };

  render() {
    if (this.state.hasError) {
      return (
        <div
          role="alert"
          style={{
            minHeight: '100vh',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            background: 'var(--bg, #0f0f0f)',
            color: 'var(--fg, #f5f5f5)',
            fontFamily: 'system-ui, sans-serif',
            gap: '1rem',
            padding: '2rem',
            textAlign: 'center',
          }}
        >
          <h2 style={{ fontSize: '1.25rem', fontWeight: 600 }}>Algo salió mal / Something went wrong</h2>
          <p style={{ fontSize: '0.875rem', opacity: 0.6, maxWidth: '32rem' }}>
            {this.state.errorMessage}
          </p>
          <button
            onClick={this.handleReload}
            style={{
              marginTop: '0.5rem',
              padding: '0.5rem 1.5rem',
              borderRadius: '0.5rem',
              border: '1px solid var(--glass-border, #444)',
              background: 'var(--glass-bg, #1a1a1a)',
              color: 'var(--fg, #f5f5f5)',
              cursor: 'pointer',
              fontSize: '0.875rem',
            }}
          >
            Recargar / Reload
          </button>
        </div>
      );
    }
    return this.props.children;
  }
}

// Protected Route Component with role-based access
function ProtectedRoute({ children }) {
  const { isAuthenticated, loading, user, tenant } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  // Role-based route protection
  if (user && !canAccessRoute(user.role, location.pathname)) {
    return <Navigate to="/" replace />;
  }

  // Tenant-layout module gating: hide postventa for cobranza tenants
  // (config-driven via tenant.analytics_layout). Blocks deep-linking too.
  if (location.pathname.startsWith('/postventa') && isPostventaHiddenForTenant(tenant)) {
    return <Navigate to="/" replace />;
  }

  return children;
}

// Public Route Component (redirects to dashboard if already logged in)
function PublicRoute({ children }) {
  const { isAuthenticated, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (isAuthenticated) {
    return <Navigate to="/" replace />;
  }

  return children;
}

// Exported so the route wiring (settings route, catch-all) can be regression-tested
// behind a MemoryRouter without mounting the full App shell.
export function AppRoutes() {
  return (
    <Routes>
      {/* Public Routes */}
      <Route
        path="/login"
        element={
          <PublicRoute>
            <Login />
          </PublicRoute>
        }
      />
      <Route
        path="/forgot-password"
        element={
          <PublicRoute>
            <ForgotPassword />
          </PublicRoute>
        }
      />
      <Route
        path="/reset-password/:token"
        element={
          <PublicRoute>
            <ResetPassword />
          </PublicRoute>
        }
      />
      <Route
        path="/invite/:token"
        element={<Invite />}
      />

      {/* Protected Routes */}
      <Route
        path="/"
        element={
          <ProtectedRoute>
            <Layout>
              <Dashboard />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/agents"
        element={
          <ProtectedRoute>
            <Layout>
              <Agents />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/agents/new"
        element={
          <ProtectedRoute>
            <Layout>
              <AgentEditor />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/agents/:id/edit"
        element={
          <ProtectedRoute>
            <Layout>
              <AgentEditor />
            </Layout>
          </ProtectedRoute>
        }
      />
      {/* Campaign Routes */}
      <Route
        path="/campaigns"
        element={
          <ProtectedRoute>
            <Layout>
              <CampaignsList />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/campaigns/new"
        element={
          <ProtectedRoute>
            <Layout>
              <CampaignWizard />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/campaigns/:id/edit"
        element={
          <ProtectedRoute>
            <Layout>
              <CampaignWizard />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/campaigns/:id"
        element={
          <ProtectedRoute>
            <Layout>
              <CampaignDetail />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/tools"
        element={
          <ProtectedRoute>
            <Layout>
              <Tools />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/manual"
        element={
          <ProtectedRoute>
            <Layout>
              <Docs />
            </Layout>
          </ProtectedRoute>
        }
      />
      {/*
        Legacy in-app redirect: the user manual used to live at /docs, which
        collides with FastAPI's reserved Swagger UI route (docs_url="/docs").
        Behind the API/proxy that route serves Swagger/OpenAPI, never the SPA.
        Any remaining in-app link to /docs now redirects to the real manual.
        (Direct browser hits to /docs still reach FastAPI Swagger by design.)
      */}
      <Route path="/docs" element={<Navigate to="/manual" replace />} />
      <Route
        path="/whatsapp"
        element={
          <ProtectedRoute>
            <Layout>
              <WhatsAppPage />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/support"
        element={
          <ProtectedRoute>
            <Layout>
              <SupportPage />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/postventa"
        element={
          <ProtectedRoute>
            <Layout>
              <PostventaPage />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/calendar"
        element={
          <ProtectedRoute>
            <Layout>
              <CalendarPage />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/calls"
        element={
          <ProtectedRoute>
            <Layout>
              <Calls />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/call-history"
        element={
          <ProtectedRoute>
            <Layout>
              <CallHistory />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/analytics"
        element={
          <ProtectedRoute>
            <Layout>
              <Analytics />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/phone-numbers"
        element={
          <ProtectedRoute>
            <Layout>
              <PhoneNumbers />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/team"
        element={
          <ProtectedRoute>
            <Layout>
              <Team />
            </Layout>
          </ProtectedRoute>
        }
      />
      {/*
        Configuración / Settings — tenant-level settings (Cadencia de contacto
        Postventa card). The original /settings route was dropped in 9d4ea904e
        (team management), which left the card unreachable; re-added here on a
        lean page. Access: admin/super_admin edit, manager read-only (ROUTE_ACCESS).
      */}
      <Route
        path="/settings"
        element={
          <ProtectedRoute>
            <Layout>
              <SettingsPage />
            </Layout>
          </ProtectedRoute>
        }
      />
      {/* Unknown paths never render a blank page: send them to the dashboard. */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <AppErrorBoundary>
    <Suspense fallback={<div className="min-h-screen flex items-center justify-center bg-background"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div></div>}>
    <ThemeProvider>
    <Router>
      <AuthProvider>
        {/* translate="no": React owns this subtree's DOM; browser auto-translate
            re-parenting text nodes here triggers the removeChild NotFoundError
            whitescreen. The app has its own i18n, so opt out of browser translation. */}
        <div className="min-h-screen bg-background text-foreground" translate="no">
          <TestingBadge />
          <ErrorBoundary>
            <AppRoutes />
          </ErrorBoundary>
          <Toaster
            position="top-right"
            toastOptions={{
              duration: 4000,
              className: 'glass-panel !rounded-xl !text-sm',
              style: {
                background: 'var(--glass-bg)',
                color: 'var(--fg)',
                border: '1px solid var(--glass-border)',
                backdropFilter: 'saturate(180%) blur(20px)',
                WebkitBackdropFilter: 'saturate(180%) blur(20px)',
                boxShadow: 'var(--glass-shadow)',
              },
              success: {
                duration: 3000,
                iconTheme: {
                  primary: '#30d158',
                  secondary: '#ffffff',
                },
              },
              error: {
                duration: 5000,
                iconTheme: {
                  primary: '#ff453a',
                  secondary: '#ffffff',
                },
              },
            }}
          />
        </div>
      </AuthProvider>
    </Router>
    </ThemeProvider>
    </Suspense>
    </AppErrorBoundary>
  );
}

export default App;
